import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';

import {
  InstitutionalIndicationOrderType,
  InterestLevelInterestUnit,
  InterestLevelLimitType,
  SecurityType,
} from '../../../../../../../../../graphql';
import { getInterestUnitOptions } from '../../../../../../../../../types/domain/order-book/constants';
import { institutionalIndicationInterestLevelOrderTypeOptions } from '../../../../../../../../order-book/constants';
import { ConvertibleOrderType } from '../../utils/getConvertibleOrderType';
import type { InterestLevelValue } from '../../utils/IndicationSectionValidation';

const getIsOrderTypeFieldHidden = (
  interestLevels: InterestLevelValue[],
  isCurrentRowOrderTypeLimit: boolean
) => {
  const levelsContainMarket = interestLevels.some(
    ({ orderType }) => orderType === InstitutionalIndicationOrderType.Market
  );

  return levelsContainMarket && isCurrentRowOrderTypeLimit;
};

export const getIsLimitTypeFieldHidden = (
  interestLevels: InterestLevelValue[],
  currentRowIndex: number
) => {
  const firstLimitTypeRowIndex = interestLevels.findIndex(
    ({ limitType, orderType }) => limitType && orderType === InstitutionalIndicationOrderType.Limit
  );

  return firstLimitTypeRowIndex !== -1 && currentRowIndex !== firstLimitTypeRowIndex;
};

export const getInterestUnitFieldOptions = (
  isOnlySharesUnitOnIndicationsEnabled: boolean,
  isDepositoryReceiptEnabled: boolean,
  securityType: SecurityType | null
) => {
  const sharesLabelOverwrite =
    securityType === SecurityType.CommonOrdinaryShare && isDepositoryReceiptEnabled
      ? 'ADS/ADR'
      : null;

  if (isOnlySharesUnitOnIndicationsEnabled) {
    return getInterestUnitOptions(sharesLabelOverwrite).filter(option => {
      return option.value === InterestLevelInterestUnit.Shares;
    });
  }

  return getInterestUnitOptions(sharesLabelOverwrite);
};

export type GetOrderUnitFieldPropsParams = {
  currentOrderType: InstitutionalIndicationOrderType | ConvertibleOrderType | null;
  interestLevels: InterestLevelValue[];
  isLimitIndicationsOnlyEnabled: boolean;
};

export const getOrderTypeUnitFieldProps = (args: GetOrderUnitFieldPropsParams) => {
  const { currentOrderType, interestLevels, isLimitIndicationsOnlyEnabled } = args;
  const isOrderTypeLimit = currentOrderType === InstitutionalIndicationOrderType.Limit;
  const hidden = getIsOrderTypeFieldHidden(interestLevels, isOrderTypeLimit);

  if (isLimitIndicationsOnlyEnabled) {
    return {
      hidden: hidden || isOrderTypeLimit,
      options: institutionalIndicationInterestLevelOrderTypeOptions.filter(option => {
        return (
          option.value === InstitutionalIndicationOrderType.Limit ||
          option.value === currentOrderType
        );
      }),
    };
  }

  return {
    hidden,
    options: institutionalIndicationInterestLevelOrderTypeOptions,
  };
};

export type UpdateInterestLevelsUnitParams = {
  currentInterestQuantity: number | null;
  currentInterestUnit: InterestLevelInterestUnit | null;
  newInterestUnit: InterestLevelInterestUnit | null;
  interestLevels: InterestLevelValue[];
};

export const updateInterestLevelsUnit = ({
  interestLevels,
  newInterestUnit,
  currentInterestUnit,
  currentInterestQuantity,
}: UpdateInterestLevelsUnitParams) => {
  return interestLevels
    .map<InterestLevelValue>(level => {
      return { ...level, interestUnit: newInterestUnit };
    })
    .map<InterestLevelValue>(level => {
      if (!isNil(currentInterestUnit) || isNil(level.interestQuantity)) {
        return level;
      }

      if (newInterestUnit === InterestLevelInterestUnit.Percent) {
        return {
          ...level,
          interestQuantity: level.interestQuantity / 100,
        };
      }

      if (newInterestUnit === InterestLevelInterestUnit.Shares) {
        return {
          ...level,
          interestQuantity: Math.round(level.interestQuantity),
        };
      }

      return level;
    })
    .map<InterestLevelValue>(level => {
      if (!isNil(currentInterestUnit) && !isNil(currentInterestQuantity)) {
        return { ...level, interestQuantity: null };
      }

      return level;
    });
};

export type UpdateInterestLevelsOrderTypeParams = {
  currentRowIndex: number;
  defaultLimitType: InterestLevelLimitType | null;
  newOrderType: InstitutionalIndicationOrderType | ConvertibleOrderType | null;
  interestLevels: InterestLevelValue[];
};

export const updateInterestLevelsOrderType = ({
  defaultLimitType,
  currentRowIndex,
  newOrderType,
  interestLevels,
}: UpdateInterestLevelsOrderTypeParams) => {
  if (newOrderType === InstitutionalIndicationOrderType.Market) {
    return interestLevels.map<InterestLevelValue>((level, index) => {
      const isCurrentLevel = currentRowIndex === index;

      if (isCurrentLevel) {
        return { ...level, limitPrice: null, limitType: null, orderType: newOrderType };
      }

      return {
        ...level,
        limitPrice: null,
        limitType: defaultTo(defaultLimitType, level.limitType),
        orderType: InstitutionalIndicationOrderType.Limit,
      };
    });
  }

  if (newOrderType === InstitutionalIndicationOrderType.Limit) {
    return interestLevels.map<InterestLevelValue>((level, index) => {
      const isCurrentLevel = currentRowIndex === index;

      return isCurrentLevel
        ? { ...level, limitType: defaultLimitType, orderType: newOrderType }
        : level;
    });
  }

  return interestLevels;
};
